import type { Badge, KV } from './types';

/** $-prefixed, thousands-separated currency, matching the original fmt(). */
export function fmt(n: number): string {
  return '$' + Number(n).toLocaleString('en-US');
}

const GREEN = ['Awarded', 'Active', 'Matched', 'Paid', 'Resolved', 'Verified', 'Disclosed', 'Low', 'Enabled'];
const AMBER = ['Under Review', 'Pending Approval', 'Pending GR', 'Pending', 'Under Investigation', 'Medium', 'In Negotiation'];
const RED = ['Cancelled', 'Suspended', 'Variance', 'Open', 'Expired', 'Not Disclosed', 'High'];
const BLUE = ['Sent', 'Received'];

export function badge(status: string): Badge {
  if (GREEN.includes(status)) return { bg: 'var(--color-success-bg)', fg: 'var(--color-success-ink)' };
  if (AMBER.includes(status)) return { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-ink)' };
  if (RED.includes(status)) return { bg: 'var(--color-error-bg)', fg: 'var(--color-error-ink)' };
  if (BLUE.includes(status)) return { bg: 'var(--color-blue-100)', fg: 'var(--color-primary-ink)' };
  return { bg: 'var(--color-ink-50)', fg: 'var(--color-fg-2)' };
}

/** { stBg, stFg } spread helper — the original mapBadge(). */
export function mapBadge(status: string): { stBg: string; stFg: string } {
  const b = badge(status);
  return { stBg: b.bg, stFg: b.fg };
}

export function riskColor(score: number): string {
  return score >= 60 ? 'var(--color-error-ink)' : score >= 30 ? 'var(--color-warning-ink)' : 'var(--color-success-ink)';
}

export function riskWord(score: number): string {
  return score >= 60 ? 'High' : score >= 30 ? 'Medium' : 'Low';
}

export function kv(k: string, v: string, font?: string): KV {
  return { k, v, font: font || 'var(--font-sans)' };
}

export interface CRow {
  label: string;
  value: string;
  best: boolean;
  font: string;
  bg: string;
  color: string;
}

export function crow(label: string, value: string, best: boolean, font?: string): CRow {
  return {
    label,
    value,
    best: !!best,
    font: font || 'var(--font-sans)',
    bg: best ? 'var(--color-success-bg)' : 'transparent',
    color: best ? 'var(--color-success)' : 'var(--color-fg-1)',
  };
}

/** Zebra shade used across tables. */
export function zebra(i: number): string {
  return i % 2 ? 'var(--color-white)' : 'rgba(247,248,250,.5)';
}

/** Alternate zebra used by the reconstructed tail screens. */
export function zeb(i: number): string {
  return i % 2 ? 'var(--color-mist)' : 'var(--color-white)';
}

/** Recent-activity / notification chip colouring by kind. */
export function activity(icon: string, kind: 'alert' | 'ok' | 'action' | 'info', text: string, time: string) {
  const color =
    kind === 'alert' ? 'var(--color-error)' :
    kind === 'ok' ? 'var(--color-success)' :
    kind === 'action' ? 'var(--color-warning)' : 'var(--color-blue)';
  const bg =
    kind === 'alert' ? 'var(--color-error-bg)' :
    kind === 'ok' ? 'var(--color-success-bg)' :
    kind === 'action' ? 'var(--color-warning-bg)' : 'var(--color-blue-100)';
  return { icon, color, bg, text, time };
}
